import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  ScrollView,
  View,
  ActivityIndicator,
  Platform,
  Linking,
} from 'react-native';
import { Text } from 'react-native-elements';
import { useSelector, useDispatch } from 'react-redux';
import { NativeModules } from 'react-native';
import _ from 'lodash';
import AsyncStorage from '@react-native-async-storage/async-storage';
import DeviceInfo from 'react-native-device-info';

import { CONDO_CORP_NAME } from '../../lib/constants';
import { ExploreProps, RouteNames } from '../../navigation/condoTypes';
import { RootState } from '../../store/rootReducer';
import Carousel, { DisplayType } from '../../components/Carousel';
import Divider from '../../components/Divider';
import { fetchAdvertCategories } from '../../store/advertCategoriesSlice';
import config from '../../lib/config';
import SearchBar from '../../components/SearchBar';

import {
  fetchAdvertsList,
  selectCategoryAdvertsList,
  selectFeaturedList,
} from '../../store/advertsSlice';

import CategoryList from '../../components/CategoryList';
import { Advertisement, FullCategory, Tag } from '../../api/adserver';
import ShiftSuiteMenuItems, {
  ShiftSuiteMenuItemId,
} from '../../lib/shiftSuiteMenu';
import CondoLandingHero from '../../components/CondoLandingHero';
import {
  selectCurrentUser,
  actionLogout,
  selectUserData,
  showModal,
  hideModal,
  selectDontShowContests,
} from '../../store/appSlice';
import { ModalName } from '../../components/RootOverlays';
import { trackScreenView } from '../../lib/analytics';
import { getContest, selectContest } from '../../store/contestSlice';
import {
  clearNotification,
  selectPushNotification,
} from '../../store/pushNotificationSlice';
import { getPropertyManager } from '../../store/propertyManagerSlice';

const debug = require('debug')('se:condolandingscreen');

type Props = {} & ExploreProps;

const CondoLandingScreen: React.FC<Props> = ({ navigation }: any) => {
  const dispatch = useDispatch();

  const {
    featuredList,
    isLoading: isLoadingFeaturedList,
    featuredCategory,
  } = useSelector((state) => selectFeaturedList(state as RootState));

  const { categoryAdvertList } = useSelector((state) =>
    selectCategoryAdvertsList(state as RootState),
  );

  const [shownContestModalId, setShownContestModalId] = useState<number>(-1);

  const [overlayCategory, setOverlayCategory] = useState<FullCategory | null>(
    null,
  );

  const { corpName, propertyImageUrl, propertyName, unitId, username } =
    useSelector((state) => selectCurrentUser(state as RootState));

  const {
    loading: contestLoading,
    isSucess: contestLoaded,
    errMsg: contestError,
    data: contestData,
  } = useSelector((state) => selectContest(state as RootState));

  useEffect(() => {
    if (corpName) {
      dispatch(fetchAdvertCategories(corpName));
      dispatch(fetchAdvertsList(corpName));
      dispatch(getContest(corpName));
    }
  }, [dispatch, corpName]);

  useEffect(() => {
    dispatch(getPropertyManager());
  }, [dispatch]);

  useEffect(() => {
    trackScreenView('condo_landing');
  }, []);

  const userData = useSelector((state) => selectUserData(state as RootState));
  const tenantData = useMemo(() => {
    const Tenants = _.get(userData, 'Tenants', []);
    let tenantCore;
    if (userData && _.isArray(Tenants)) {
      tenantCore = _.get(userData, 'Tenants[0]', null);
    } else {
      tenantCore = _.get(userData, 'Tenants', null);
    }
    const unit = _.get(userData, 'Unit.Identification.attributes.IDValue', '');
    const personalData: any = _.get(tenantCore, 'PersonDetails', null);
    let firstName;
    let lastName;
    let fullName;
    if (personalData) {
      firstName = _.get(personalData, 'Name.FirstName', '');
      if (firstName) {
        fullName = firstName;
      }
      lastName = _.get(personalData, 'Name.LastName', '');
      if (lastName) {
        if (fullName.length > 0) {
          fullName = `${fullName} ${lastName}`;
        } else {
          fullName = lastName;
        }
      }
    }
    let property;
    const propertyData: any = _.get(userData, 'Property', null);
    return {
      firstName,
      fullName,
      lastName,
      personalData,
      propertyData,
      tenantCore,
      unit,
    };
  }, [userData]);

  const onAdvertCateOverlayClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const dontShowContests = useSelector((state) =>
    selectDontShowContests(state as RootState),
  );
  const [hideContest, setHideContest] = useState<boolean>(true);

  useEffect(() => {
    console.log(
      `${contestLoaded}, ${_.isEmpty(
        contestData,
      )}, ${shownContestModalId}, ${dontShowContests}`,
    );
    if (!contestLoaded || _.isEmpty(contestData)) {
      return;
    }
    if (
      !_.isEmpty(dontShowContests) &&
      dontShowContests.includes(contestData.id)
    ) {
      return;
    }
    if (
      contestLoaded &&
      !_.isEmpty(contestData) &&
      shownContestModalId !== contestData.id
    ) {
      setShownContestModalId(contestData.id);
      dispatch(
        showModal({
          modalName: ModalName.ContestInfo,
          isShowModal: true,
          modalProps: {
            onClose: onAdvertCateOverlayClose,
            navigation,
          },
        }),
      );
    }
  }, [
    contestLoaded,
    contestData,
    dispatch,
    shownContestModalId,
    setShownContestModalId,
    onAdvertCateOverlayClose,
    dontShowContests,
    navigation,
  ]);

  useEffect(() => {
    if (!contestLoaded || _.isEmpty(contestData)) {
      setHideContest(true);
      return;
    }
    setHideContest(false);
  }, [contestLoaded, contestData]);

  const menuItems = useMemo(() => {
    if (hideContest) {
      return ShiftSuiteMenuItems.filter(
        (item) => item.id !== ShiftSuiteMenuItemId.contest,
      );
    }
    return ShiftSuiteMenuItems;
  }, [hideContest]);

  const pushData = useSelector((state) =>
    selectPushNotification(state as RootState),
  );
  useEffect(() => {
    console.log('pushData is:', pushData);
    if (isLoadingFeaturedList) {
      console.log('push effect isLoadingFeaturedList');
      // Alert.alert('push effect isLoadingFeaturedList');
      return;
    }
    if (typeof pushData.pushNotification === 'string') {
      return;
    }
    if (
      !pushData ||
      !pushData.pushNotification ||
      !pushData.pushNotification?.data?.screen
    ) {
      console.log('push effect no push data');
      return;
    }
    if (!navigation) {
      console.log('push effect no navigation');
      return;
    }
    // Alert.alert('push effect no push data', JSON.stringify(pushData));
    console.log(
      `pushData targetting screen is: ${pushData.pushNotification?.data.screen}, ${pushData.pushNotification?.data.content_id}, ${pushData.pushNotification?.messageId}`,
    );
    navigation.navigate(pushData.pushNotification?.data.screen, {
      contentId: pushData.pushNotification?.data.content_id,
      messageId: pushData.pushNotification?.messageId,
    });
    dispatch(clearNotification());
  }, [dispatch, navigation, pushData, isLoadingFeaturedList]);

  const navigateToExplore = useCallback(
    (category: string, tagParam?: Tag) => {
      let tag;
      if (tagParam) {
        tag = JSON.stringify(tagParam);
      }
      navigation.navigate(RouteNames.ExploreTab, {
        screen: RouteNames.Explore,
        params: { query: category, tag, key: Math.random() },
      });
    },
    [navigation],
  );

  const heroProps = {
    corpName: 'My Corp Name',
    propertyImageUrl,
    propertyName: tenantData.propertyData
      ? tenantData.propertyData.MarketingName
      : null,
    unitId: tenantData.unit,
    username: tenantData.fullName,
  };

  const onAdvertOverlay = (item: Advertisement, category: FullCategory) => {
    debug('***** -- item advert was pressed', item.id);
    setOverlayCategory(category);
    dispatch(
      showModal({
        modalName: ModalName.Advert,
        isShowModal: true,
        modalProps: {
          onClose: onAdvertOverlayClose,
          item,
          onPressTag: (tag: Tag) => handleOnPressTag(tag, category),
        },
      }),
    );
  };

  const onPressFindMore = (item: FullCategory) => {
    debug('onPressFindMore was pressed', item.id);
    navigateToExplore(item.title);
  };

  const onAdvertOverlayClose = () => {
    dispatch(hideModal());
  };

  const onAdvertCateOverlay = useCallback(
    (category: FullCategory) => {
      //Just hardcode for navigate to NewsScreen
      if (category && category.id === 'News') {
        return navigation.navigate(RouteNames.News);
      } else if (category && category.id === 'Gallery') {
        return navigation.navigate(RouteNames.Gallery);
      } else if (
        category &&
        category.id === ShiftSuiteMenuItemId.communityCalendar
      ) {
        return navigation.navigate(RouteNames.CommunityCalendar);
      } else if (
        category &&
        category.id === ShiftSuiteMenuItemId.propertyManager
      ) {
        return dispatch(
          showModal({
            modalName: ModalName.PropertyManager,
            isShowModal: true,
            modalProps: {
              onClose: onAdvertCateOverlayClose,
            },
          }),
        );
      } else if (category && category.id === ShiftSuiteMenuItemId.contactUs) {
        return dispatch(
          showModal({
            modalName: ModalName.ContactUs,
            isShowModal: true,
            modalProps: {
              onClose: onAdvertCateOverlayClose,
            },
          }),
        );
      } else if (category && category.id === ShiftSuiteMenuItemId.signOut) {
        AsyncStorage.setItem(CONDO_CORP_NAME, '').then(() => {
          dispatch(actionLogout());
        });
        return;
      } else if (category && category.id === ShiftSuiteMenuItemId.ledger) {
        return navigation.navigate(RouteNames.Ledger);
      } else if (
        category &&
        category.id === ShiftSuiteMenuItemId.serviceRequests
      ) {
        return navigation.navigate(RouteNames.ServiceRequest);
      } else if (category && category.id === ShiftSuiteMenuItemId.feedback) {
        return navigation.navigate(RouteNames.Feedback);
      } else if (category && category.id === ShiftSuiteMenuItemId.contest) {
        return navigation.navigate(RouteNames.Contest);
      } else if (
        category &&
        category.id === ShiftSuiteMenuItemId.statusCertificate
      ) {
        const statusUrl = 'https://www.statuscertificate.com/';
        Linking.canOpenURL(statusUrl).then((supported) => {
          if (supported) {
            Linking.openURL(statusUrl);
          } else {
            console.log("Don't know how to open URI: " + statusUrl);
          }
        });
        return;
      }
      navigateToExplore(category.title);
    },
    [dispatch, navigateToExplore, navigation, onAdvertCateOverlayClose],
  );

  const handleOnPressTag = (tag: Tag, category: FullCategory) => {
    debug(`tag: ${tag.id} pressed`);
    if (!category) {
      debug('no overlay category is set.  cannot process handleOnPressTag');
      return;
    }
    onAdvertOverlayClose();
    debug('navigateToCondoFeature is not available in native bridge');
    navigateToExplore(category.title, tag);
  };

  const onPressViewAllFeatured = () => {
    debug('onPressViewAllFeatured was pressed');
    if (NativeModules.RNAction && NativeModules.RNAction.navigateToTab) {
      debug('navigating to tab Explore');
      NativeModules.RNAction.navigateToTab('Explore');
    } else {
      debug('navigateToTab is not available in native bridge');
      navigateToExplore('');
    }
  };

  const showLoading =
    isLoadingFeaturedList && (!featuredList || featuredList.length === 0);

  return (
    <SafeAreaView style={styles.container}>
      <SearchBar placeholder="Search" />
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        style={[styles.scrollView]}
      >
        <CondoLandingHero {...heroProps} />
        <Divider />
        <View style={styles.contentContainer}>
          <Carousel
            name="shiftsuitemenuitems"
            displayType={DisplayType.mini}
            items={menuItems}
            onPressData={onAdvertCateOverlay}
          />
        </View>
        <Divider />
        {showLoading && <ActivityIndicator style={styles.loadingIndicator} />}
        {!showLoading && (
          <>
            <View
              style={[styles.contentContainer, styles.sectionEnd]}
              renderToHardwareTextureAndroid
            >
              <View
                style={[styles.featureHeadingContainer, styles.sectionStart]}
              >
                <Text style={styles.featureHeading}>Featured</Text>
              </View>
              <Carousel
                loop
                name="shiftsuitefeatured"
                displayType={DisplayType.featured}
                onPress={(item: Advertisement) =>
                  featuredCategory && onAdvertOverlay(item, featuredCategory)
                }
                onPressCategory={onPressFindMore}
                items={featuredList}
                onViewMore={onPressViewAllFeatured}
                showViewMore={true}
              />
            </View>
            <Divider large />
            <View
              style={styles.categoryListContainer}
              renderToHardwareTextureAndroid
            >
              <CategoryList
                advertCategoryList={categoryAdvertList}
                onPress={onAdvertOverlay}
                onPressCategory={onPressFindMore}
              />
            </View>
            <Text
              style={styles.debugView}
            >{`# ${DeviceInfo.getVersion()}`}</Text>
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#333333',
    flex: 1,
  },
  contentContainer: {
    marginStart: 15,
    ...Platform.select({
      android: {
        flex: 1,
      },
      ios: {
        flex: 1,
      },
    }),
  },
  categoryListContainer: {
    flex: 1,
    marginStart: 10,
  },
  scrollView: {
    backgroundColor: '#ffffff',
    marginHorizontal: 0,
  },
  loadingIndicator: {
    margin: 20,
  },
  engine: {
    position: 'absolute',
    right: 0,
  },
  sectionContainer: {
    marginTop: 32,
    paddingHorizontal: 24,
  },
  featureHeadingContainer: {
    flexDirection: 'row',
    marginBottom: 5,
    justifyContent: 'space-between',
    marginEnd: config.theme.headingEndMargin,
  },
  sectionStart: {
    marginTop: 25,
    marginBottom: 8,
  },
  sectionEnd: {
    marginBottom: 15,
  },
  featureHeading: {
    fontSize: config.theme.headingFontSize,
    fontWeight: config.theme.headingFontWeight,
    color: config.theme.headingTextColor,
  },
  featureViewAll: {
    fontSize: 14,
    fontWeight: 'bold',
    alignSelf: 'center',
    color: config.theme.headingTextColor,
  },
  debugView: {
    backgroundColor: 'white',
    color: '#ccc',
    textAlign: 'center',
    fontSize: 8,
    marginTop: 6,
  },
});

export default CondoLandingScreen;
